// Moteur Topola (Apache-2.0) : rendu SVG généalogique natif (D3), types de
// chart Sablier / Descendants / Ascendance. On lui fournit notre modèle au
// format JsonGedcomData. Export via sérialisation du <svg> rendu (exportTree).
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { createChart, DetailedRenderer, HourglassChart, DescendantChart, AncestorChart } from 'topola';
import { Membre, Relation } from '../../types';
import { toTopolaJson } from '../../utils/genealogyModel';
import { exportSvgToPdf, exportSvgToPng } from '../../utils/exportTree';
import { EngineExportHandle } from './engineTypes';

export type TopolaKind = 'hourglass' | 'descendants' | 'ancestors';
// Les classes Topola sont génériques : on les traite comme des valeurs opaques.
const CHART_TYPES: Record<TopolaKind, unknown> = {
  hourglass: HourglassChart,
  descendants: DescendantChart,
  ancestors: AncestorChart,
};

interface Props {
  membres: Membre[];
  relations: Relation[];
  rootId: number | null;
  kind: TopolaKind;
}

let seq = 0;

const TopolaChart = forwardRef<EngineExportHandle, Props>(function TopolaChart(
  { membres, relations, rootId, kind }, ref,
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const idRef = useRef('topola-svg-' + (++seq));

  const svgEl = () => hostRef.current?.querySelector('svg') as SVGSVGElement | null;
  useImperativeHandle(ref, () => ({
    exportPdf: () => { const el = svgEl(); return el ? exportSvgToPdf(el, 'arbre-topola.pdf') : undefined; },
    exportPng: () => { const el = svgEl(); return el ? exportSvgToPng(el, 'arbre-topola.png', 2) : undefined; },
  }));

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const json = toTopolaJson(membres, relations);
    if (!json.indis.length) { host.innerHTML = ''; return; }
    const start = rootId != null ? 'I' + rootId : json.indis[0].id;
    host.innerHTML = `<svg id="${idRef.current}" width="100%" height="600"></svg>`;
    try {
      const chart = createChart({
        json,
        chartType: CHART_TYPES[kind] as never,
        renderer: DetailedRenderer as never,
        svgSelector: '#' + idRef.current,
        horizontal: false,
      });
      chart.render({ startIndi: start });
    } catch { /* rendu impossible : laisse le svg vide */ }
  }, [membres, relations, rootId, kind]);

  return <div ref={hostRef} style={{ width: '100%', minHeight: 460, overflow: 'auto' }} />;
});

export default TopolaChart;
